import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import { ChangeHistory } from "@/common/components/ChangeHistory"
import { AddressSection } from "@/features/privateCompanyLeadsFunnel/components/AddressSection"
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist"
import { ContactsSection } from "@/features/privateCompanyLeadsFunnel/components/ContactsSection"
import { GeneralInformationSection } from "@/features/privateCompanyLeadsFunnel/components/GeneralInformationSection"
import { GenericDocumentsSection } from "@/features/privateCompanyLeadsFunnel/components/GenericDocumentsSection"
import { LeadHeader } from "@/features/privateCompanyLeadsFunnel/components/LeadHeader"
import { OperationalInformationSection } from "@/features/privateCompanyLeadsFunnel/components/OperationalInformationSection"
import { ResponsibleSection } from "@/features/privateCompanyLeadsFunnel/components/ResponsibleSection"
import { stateStrategy, translateStateStrategy } from "@/features/privateCompanyLeadsFunnel/constants/stateStrategy.constant"
import useAdvancePrivateCompanyLead from "@/features/privateCompanyLeadsFunnel/hooks/useAdvancePrivateCompanyLead"
import usePrivateCompanyLeadForm from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLeadForm"
import { LeadFormStateProps } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { FormProvider } from "react-hook-form"
import { BillDetailsSection } from "../BillDetailsSection"

const LeadQualifyingForm = ({ id, privateCompanyLead }: LeadFormStateProps) => {
  const {
    serverError,
    formMethods,
    passedEveryRequirement,
    redirectOnSuccess,
    onError,
    submit,
    submitManagers,
    submittableOnBlur,
    updatePrivateCompanyLeadIsPending,
    updatePrivateCompanyLeadManagersIsPending,
  } = usePrivateCompanyLeadForm({ privateCompanyLead });

  const {
    control,
    register,
    setValue,
    setError,
    watch,
    formState: { errors, }
  } = formMethods;

  const {
    mutate: advancePrivateCompanyLead,
    isPending: advancePrivateCompanyLeadIsPending
  } = useAdvancePrivateCompanyLead({
    id,
    onSuccess: () => redirectOnSuccess('/private-clients'),
    onError
  });

  return (
    <FormProvider {...formMethods}>
      <form>
        <LeadHeader
          privateCompanyLeadId={id}
          display_name={privateCompanyLead.display_name}
          url={privateCompanyLead.general_information?.company_logo?.url}
          showArchiveButton
          archived_at={privateCompanyLead.archived_at}
          buttons={
            <div className="flex gap-2">
              <Button
                type="button"
                className={passedEveryRequirement ? "shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" : ''}
                variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                disabled={!passedEveryRequirement}
                onClick={() => advancePrivateCompanyLead()}
                isLoading={advancePrivateCompanyLeadIsPending}
                name="advance_lead"
              >
                Enviar para análise do gestor
              </Button>
            </div>
          }
        />
        <div className='flex gap-6'>
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll">
            {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
            <ResponsibleSection
              privateCompanyLead={privateCompanyLead}
              submit={submitManagers}
              submitObservations={submit}
              disabled={advancePrivateCompanyLeadIsPending || updatePrivateCompanyLeadManagersIsPending}
            />
            <OperationalInformationSection
              operational_information={privateCompanyLead.operational_information}
              control={control}
              register={register}
              errors={errors}
              submit={submit}
              submittableOnBlur={submittableOnBlur}
              disabled={advancePrivateCompanyLeadIsPending || updatePrivateCompanyLeadIsPending}
            />
            <GeneralInformationSection
              general_information={privateCompanyLead.general_information}
              register={register}
              errors={errors}
              submittableOnBlur={submittableOnBlur}
              submit={submit}
              disabled={advancePrivateCompanyLeadIsPending || updatePrivateCompanyLeadIsPending}
            />
            <BillDetailsSection
              contract={privateCompanyLead.contract}
              control={control}
              watch={watch}
              register={register}
              disabled
            />
            <AddressSection
              address={privateCompanyLead.address}
              setValue={setValue}
              setError={setError}
              errors={errors}
              submit={submit}
              disabled={advancePrivateCompanyLeadIsPending || updatePrivateCompanyLeadIsPending}
            />
            <ContactsSection
              privateCompanyLeadId={id}
              leadState={privateCompanyLead?.state}
              contacts={privateCompanyLead?.contacts}
              contact_for_master_user={privateCompanyLead?.contact_for_master_user}
              control={control}
              redirectOnSuccess={redirectOnSuccess}
            />
            <GenericDocumentsSection
              privateCompanyLeadId={id}
              generic_documents={privateCompanyLead.generic_documents}
              control={control}
              disabled={advancePrivateCompanyLeadIsPending || updatePrivateCompanyLeadIsPending}
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={privateCompanyLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[privateCompanyLead.state as keyof typeof stateStrategy]]}
              Buttons={() => (
                <Button
                  type="button"
                  variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                  disabled={!passedEveryRequirement}
                  onClick={() => advancePrivateCompanyLead()}
                  isLoading={advancePrivateCompanyLeadIsPending}
                  name="advance_lead"
                >
                  Enviar para análise do gestor
                </Button>
              )}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
            />
            <ChangeHistory logs={privateCompanyLead?.logs || []} entityId={id} />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default LeadQualifyingForm
